import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar si hay un token guardado al cargar la aplicación
    const savedToken = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('authUser');

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        authService.setAuthToken(savedToken);
      } catch (error) {
        console.error('Error al cargar datos de autenticación:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const userData = response.user || { email };
      const authToken = response.token;

      if (!authToken) {
        throw new Error('No se recibió token de autenticación');
      }

      setUser(userData);
      setToken(authToken);
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('authUser', JSON.stringify(userData));
      authService.setAuthToken(authToken);

      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const response = await authService.googleLogin(credential);
      const userData = response.user || { email: response.email };
      const authToken = response.token;

      if (!authToken) {
        throw new Error('No se recibió token de autenticación');
      }

      setUser(userData);
      setToken(authToken);
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('authUser', JSON.stringify(userData));
      authService.setAuthToken(authToken);

      return response;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const logout = () => {
    try {
      // Limpiar estado local
      setUser(null);
      setToken(null);

      // Limpiar localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');

      // Limpiar servicio de autenticación
      authService.removeAuthToken();

      // Limpiar cualquier otro dato de sesión que pueda existir
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      console.log('Sesión cerrada correctamente');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const refreshToken = async () => {
    try {
      const response = await authService.refreshToken();
      const newToken = response.token;
      setToken(newToken);
      localStorage.setItem('authToken', newToken);
      authService.setAuthToken(newToken);
      return newToken;
    } catch (error) {
      console.error('Error al renovar token:', error);
      logout();
      throw error;
    }
  };

  const updateUser = userData => {
    setUser(userData);
    localStorage.setItem('authUser', JSON.stringify(userData));
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithGoogle,
    logout,
    refreshToken,
    updateUser,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
